import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { collection, doc, getDocs, query, updateDoc, where } from "firebase/firestore";
import { db } from "../firebase";
import i18n, {
  DEFAULT_LANGUAGE,
  LANGUAGE_STORAGE_KEY,
  SUPPORTED_LANGUAGES,
  normalizeLanguage,
} from "../i18n";

/**
 * Idioma efectivo de la plataforma de residente.
 *
 * Fuente de verdad: `users.idioma` (viaja con la cuenta). En el boot arranca del
 * localStorage (idioma instantáneo antes de resolver Firestore); al login se lee
 * `users.idioma` y se aplica. `setLanguage` cambia i18next + persiste en
 * localStorage y write-through a `users.idioma`.
 *
 * Se siembra del `userEmail` del USUARIO LOGUEADO REAL (no del efectivo de "Ver
 * como residente"), para que el admin conserve su idioma mientras impersona.
 */
const LanguageContext = createContext({
  language: DEFAULT_LANGUAGE,
  setLanguage: () => {},
  supported: SUPPORTED_LANGUAGES,
});

export function LanguageProvider({ userEmail, children }) {
  const [language, setLanguageState] = useState(() => normalizeLanguage(i18n.language));
  const [userDocId, setUserDocId] = useState(null);

  // Al login (o cambio de usuario): leer users.idioma y aplicarlo.
  useEffect(() => {
    let cancelled = false;
    if (!userEmail) {
      setUserDocId(null);
      return;
    }
    (async () => {
      try {
        const snap = await getDocs(query(collection(db, "users"), where("email", "==", userEmail)));
        if (cancelled || snap.empty) return;
        const d = snap.docs[0];
        setUserDocId(d.id);
        const idioma = normalizeLanguage(d.data().idioma || i18n.language);
        setLanguageState(idioma);
        if (i18n.language !== idioma) i18n.changeLanguage(idioma);
        try { localStorage.setItem(LANGUAGE_STORAGE_KEY, idioma); } catch (_) {}
      } catch (_) {
        // sin acceso al doc: se queda con el idioma del localStorage/default.
      }
    })();
    return () => { cancelled = true; };
  }, [userEmail]);

  // El atributo lang del <html> hace que los mensajes NATIVOS del navegador
  // (validación HTML5 "Completa este campo", datepickers, etc.) salgan en el idioma.
  useEffect(() => {
    try { document.documentElement.lang = language; } catch (_) {}
  }, [language]);

  const setLanguage = useCallback(
    (lang) => {
      const norm = normalizeLanguage(lang);
      setLanguageState(norm);
      if (i18n.language !== norm) i18n.changeLanguage(norm);
      try { localStorage.setItem(LANGUAGE_STORAGE_KEY, norm); } catch (_) {}
      if (userDocId) {
        updateDoc(doc(db, "users", userDocId), { idioma: norm }).catch(() => {});
      }
    },
    [userDocId]
  );

  return (
    <LanguageContext.Provider value={{ language, setLanguage, supported: SUPPORTED_LANGUAGES }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}

export default LanguageContext;
